/**
 * Admin: Employer verification queue.
 *
 * Lists pending employer signups so an admin can vet the domain + LinkedIn
 * before verifying (or rejecting) them. Verified employers unlock the
 * Talent Directory; rejected ones stay locked out.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldX, ExternalLink, RefreshCw, Building2, Mail, Calendar, Send, Eye, Linkedin, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import AdminHubNav from "../components/AdminHubNav";

const STATUS_TABS = [
  { id: "pending",  label: "Pending",  color: "#CA8A04" },
  { id: "verified", label: "Verified", color: "#0E5A4A" },
  { id: "rejected", label: "Rejected", color: "#B91C1C" },
  { id: "all",      label: "All",      color: "#475569" },
];

export default function EmployersAdmin() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busy, setBusy] = useState(null);   // employer.id currently being acted on
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestSummary, setDigestSummary] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [imperErr, setImperErr] = useState("");

  // Kick off a 15-minute read-only "View as" session, then jump the admin
  // into the Store so they immediately see what that employer sees. The
  // ImpersonationBanner in Store/Shortlist/Discover handles the countdown
  // and the Exit button; blocked writes surface as toast errors on 403.
  const impersonate = useCallback(async (empId) => {
    setImperErr("");
    setBusy(empId);
    try {
      await api.post(`/admin/employers/${empId}/impersonate`);
      navigate("/employer/store");
    } catch (err) {
      setImperErr(err?.response?.data?.detail || "Could not start impersonation.");
    } finally { setBusy(null); }
  }, [navigate]);

  const load = useCallback(async (filter = statusFilter) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/employers?status_filter=${filter}`);
      setRows(r.data.employers || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(statusFilter); }, [load, statusFilter]);

  const act = async (empId, action) => {
    const note = action === "reject"
      ? window.prompt("Reason for rejecting (visible to admins only):", "")
      : "";
    if (action === "reject" && note === null) return;
    setBusy(empId);
    try {
      await api.post(`/admin/employers/${empId}/action`, { action, note: note || "" });
      await load(statusFilter);
    } finally { setBusy(null); }
  };

  const runDigest = async () => {
    if (!window.confirm(
      "Send the weekly Talent Digest to ALL verified employers who haven't opted out?\n\n" +
      "Each recipient gets a personalized email with fresh publications, their outbox status, and trending profiles. " +
      "Employers with an empty digest (no fresh pubs + no outbox activity) are skipped."
    )) return;
    setDigestBusy(true);
    setDigestSummary(null);
    try {
      const r = await api.post("/admin/employers/digest-run", {});
      setDigestSummary(r.data.results || null);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Digest run failed. Check the backend logs.");
    } finally { setDigestBusy(false); }
  };

  // Load + persist the weekly cron schedule (day/hour/minute + enabled flag)
  const loadSchedule = useCallback(async () => {
    try {
      const r = await api.get("/admin/employers/digest-schedule");
      setSchedule(r.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const patchSchedule = async (patch) => {
    setScheduleSaving(true);
    try {
      const r = await api.patch("/admin/employers/digest-schedule", patch);
      setSchedule(r.data);
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't update schedule.");
    } finally { setScheduleSaving(false); }
  };

  return (
    <div className="min-h-screen py-6 px-4" style={{ background: "#F8FAFC" }}>
      <div className="max-w-5xl mx-auto">
        <AdminHubNav active="employers" />
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest"
                 style={{ color: "#94A3B8", fontWeight: 700 }}>· Admin · Employers</div>
            <h1 className="font-display uppercase leading-tight"
                style={{ fontSize: "1.6rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              Employer verification queue
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={runDigest}
                    disabled={digestBusy}
                    className="inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                    style={{
                      background: "#FF6A00", color: "white",
                      border: "1.5px solid #0F1B2D", fontWeight: 700,
                      boxShadow: "2px 2px 0 0 #0F1B2D",
                      cursor: digestBusy ? "wait" : "pointer",
                    }}
                    title="Send the weekly Talent Digest to all verified employers"
                    data-testid="employers-admin-run-digest">
              <Send size={11} /> {digestBusy ? "Sending…" : "Send Weekly Digest"}
            </button>
            <button onClick={() => load(statusFilter)}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                    style={{ background: "white", color: "#0F1B2D", border: "1.5px solid #0F1B2D", fontWeight: 700 }}
                    data-testid="employers-admin-refresh">
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </div>

        {digestSummary && (
          <div className="mb-4 rounded p-3 flex flex-wrap items-center gap-3 text-[12px]"
               style={{ background: "#FFF4E0", border: "1.5px dashed #FFC145", color: "#7C2D12" }}
               data-testid="employers-admin-digest-summary">
            <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ fontWeight: 700 }}>
              · Digest run complete
            </span>
            <span>· Sent: <b>{digestSummary.sent}</b></span>
            <span>· Skipped (empty): <b>{digestSummary.skipped_empty}</b></span>
            <span>· Opted out: <b>{digestSummary.opted_out}</b></span>
            <span>· No email: <b>{digestSummary.no_email}</b></span>
          </div>
        )}

        {/* Weekly digest cron schedule — read-write */}
        {schedule && (
          <div className="mb-5 rounded p-3 flex flex-wrap items-center gap-3 text-[12px]"
               style={{ background: "white", border: "1.5px solid #0F1B2D",
                        boxShadow: "2px 2px 0 0 #FFC145" }}
               data-testid="employers-admin-schedule">
            <span className="font-mono-rm uppercase text-[10px] tracking-widest"
                  style={{ color: "#FF6A00", fontWeight: 700 }}>· Weekly digest cron</span>
            <label className="inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={!!schedule.enabled}
                     onChange={(e) => patchSchedule({ enabled: e.target.checked })}
                     disabled={scheduleSaving}
                     data-testid="schedule-enabled" />
              <span style={{ color: schedule.enabled ? "#065F46" : "#94A3B8", fontWeight: 700 }}>
                {schedule.enabled ? "Enabled" : "Paused"}
              </span>
            </label>
            <span>· Day:</span>
            <select value={schedule.day_of_week || "mon"}
                    onChange={(e) => patchSchedule({ day_of_week: e.target.value })}
                    disabled={scheduleSaving}
                    className="rounded px-2 py-0.5 text-[12px]"
                    style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                    data-testid="schedule-day">
              {[["mon","Mon"],["tue","Tue"],["wed","Wed"],["thu","Thu"],["fri","Fri"],["sat","Sat"],["sun","Sun"]].map(([v,l]) =>
                <option key={v} value={v}>{l}</option>
              )}
            </select>
            <span>· Hour (UTC):</span>
            <select value={schedule.hour ?? 9}
                    onChange={(e) => patchSchedule({ hour: Number(e.target.value) })}
                    disabled={scheduleSaving}
                    className="rounded px-2 py-0.5 text-[12px]"
                    style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                    data-testid="schedule-hour">
              {Array.from({length:24}).map((_,i) =>
                <option key={i} value={i}>{String(i).padStart(2,"0") + ":00"}</option>
              )}
            </select>
            <span style={{ marginLeft: "auto", color: "#475569" }}>
              Next run:{" "}
              <b style={{ color: "#0F1B2D" }} data-testid="schedule-next-run">
                {schedule.next_run
                  ? new Date(schedule.next_run).toLocaleString(undefined, {
                      weekday: "short", month: "short", day: "numeric",
                      hour: "2-digit", minute: "2-digit", timeZoneName: "short",
                    })
                  : "not armed"}
              </b>
            </span>
          </div>
        )}

        {/* Status tabs */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="employers-admin-tabs">
          {STATUS_TABS.map((t) => (
            <button key={t.id}
                    onClick={() => setStatusFilter(t.id)}
                    className="px-3 py-1 rounded text-[11px] font-mono-rm uppercase tracking-widest"
                    style={{
                      background: statusFilter === t.id ? t.color : "white",
                      color: statusFilter === t.id ? "white" : t.color,
                      border: `1.5px solid ${t.color}`,
                      fontWeight: 700, cursor: "pointer",
                    }}
                    data-testid={`employers-admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="text-center py-10 font-mono-rm text-[11px] uppercase tracking-widest"
               style={{ color: "#94A3B8" }}>· Loading</div>
        )}

        {imperErr && (
          <div className="rounded-md p-3 mb-3 text-[12px]"
               style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#7F1D1D" }}
               data-testid="employers-admin-imper-err">
            {imperErr}
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="text-center py-12 rounded-md"
               style={{ background: "white", border: "1px dashed #CBD5E1" }}
               data-testid="employers-admin-empty">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              · No {statusFilter === "all" ? "" : statusFilter} employers
            </div>
          </div>
        )}

        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md p-3 md:p-4 flex items-start gap-3 flex-wrap"
                 style={{ background: "white", border: "1.5px solid #CBD5E1" }}
                 data-testid={`employer-row-${r.id}`}>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display uppercase leading-tight" style={{ color: "#0F1B2D" }}>
                    {r.first_name} {r.last_name}
                  </div>
                  <StatusPill status={r.status} />
                </div>
                <div className="flex items-center gap-2 flex-wrap text-[12px] mt-1"
                     style={{ color: "#475569" }}>
                  <span className="inline-flex items-center gap-1"><Building2 size={11} /> {r.company}</span>
                  <span className="inline-flex items-center gap-1"><Mail size={11} /> {r.email}</span>
                  <span className="inline-flex items-center gap-1"><Calendar size={11} /> {r.created_at?.slice(0, 10)}</span>
                </div>
                {r.linkedin_url && (
                  <a href={r.linkedin_url} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 mt-1 text-[11px]"
                     style={{ color: "#FF6A00", textDecoration: "underline" }}>
                    LinkedIn <ExternalLink size={10} />
                  </a>
                )}
                {r.admin_note && (
                  <div className="text-[11px] italic mt-1"
                       style={{ color: "#64748B" }}>· Note: {r.admin_note}</div>
                )}
                <LinkedInSignalCard verification={r.linkedin_verification} employerId={r.id} />
              </div>
              {r.status === "verified" && (
                <button
                  disabled={busy === r.id}
                  onClick={() => impersonate(r.id)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "#FFC145", color: "#0F1B2D", border: "1.5px solid #0F1B2D", fontWeight: 800 }}
                  title="Start a 15-minute read-only 'View as' session — you'll see the Store exactly as this employer sees it."
                  data-testid={`employer-view-as-${r.id}`}>
                  <Eye size={11} /> View as
                </button>
              )}
              {r.status !== "verified" && (
                <button
                  disabled={busy === r.id}
                  onClick={() => act(r.id, "verify")}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "#0E5A4A", color: "white", border: "1.5px solid #0E5A4A", fontWeight: 700 }}
                  data-testid={`employer-verify-${r.id}`}>
                  <ShieldCheck size={11} /> Verify
                </button>
              )}
              {r.status !== "rejected" && (
                <button
                  disabled={busy === r.id}
                  onClick={() => act(r.id, "reject")}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
                  style={{ background: "white", color: "#B91C1C", border: "1.5px solid #B91C1C", fontWeight: 700 }}
                  data-testid={`employer-reject-${r.id}`}>
                  <ShieldX size={11} /> Reject
                </button>
              )}
            </div>
          ))}
        </div>
        <Link to="/" className="inline-block mt-6 font-mono-rm text-[10px] uppercase tracking-widest"
              style={{ color: "#94A3B8" }}>← Dashboard</Link>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const meta = {
    pending:  { bg: "#FEF3C7", color: "#CA8A04" },
    verified: { bg: "#D1FAE5", color: "#0E5A4A" },
    rejected: { bg: "#FEE2E2", color: "#B91C1C" },
  }[status] || { bg: "#F1F5F9", color: "#64748B" };
  return (
    <span className="text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
          style={{ background: meta.bg, color: meta.color, fontWeight: 700 }}>
      {status}
    </span>
  );
}

/**
 * LinkedInSignalCard — inline "trust snapshot" beneath each pending employer.
 *
 * Renders whenever the employer has a `linkedin_verification` blob (they
 * completed the OAuth handshake at least once). Surfaces:
 *   · Signal strength pill (HIGH / MED / LOW) so an admin can 1-click
 *     approve legitimate recruiters and focus on borderline ones
 *   · Verified LinkedIn email + name so admin can compare against signup
 *   · Match reason ("email_match" / "domain_match" / "no_match") so admin
 *     knows why the auto-verifier didn't fire on its own
 *   · Timestamp of the OAuth attempt
 *
 * NOTE: We deliberately don't render this for verified employers — they
 * already have a green Verified pill; the snapshot would just be noise.
 */
function LinkedInSignalCard({ verification, employerId }) {
  if (!verification) return null;
  const {
    linkedin_email, linkedin_email_verified,
    linkedin_name, match_reason, checked_at,
    verified,
  } = verification;

  // Signal strength — surface as a colored pill the admin can eyeball
  // even from across the room. `email_match` is the strongest signal
  // because it proves the LinkedIn account owns the exact signup email;
  // `domain_match` still proves company affiliation; `no_match` means
  // human review is still required.
  const strength = (() => {
    if (match_reason === "email_match")  return { label: "HIGH · email match",   color: "#0E5A4A", bg: "#D1FAE5", border: "#10B981" };
    if (match_reason === "domain_match") return { label: "MED · domain match",   color: "#78350F", bg: "#FEF3C7", border: "#F59E0B" };
    return { label: "LOW · manual review", color: "#7F1D1D", bg: "#FEE2E2", border: "#EF4444" };
  })();

  return (
    <div
      className="rounded-md p-2 mt-2 flex items-start gap-2"
      style={{
        background: "#F0F9FF",
        border: `1.5px solid #0A66C2`,
        maxWidth: 520,
      }}
      data-testid={`linkedin-signal-${employerId}`}
    >
      <Linkedin size={14} style={{ color: "#0A66C2", marginTop: 2 }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
            style={{ background: strength.bg, color: strength.color, border: `1px solid ${strength.border}`, fontWeight: 800 }}
            data-testid={`linkedin-strength-${employerId}`}
          >
            · {strength.label}
          </span>
          {verified && (
            <span
              className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ background: "#D1FAE5", color: "#065F46", border: "1px solid #10B981", fontWeight: 800 }}
            >
              <CheckCircle2 size={9} /> Auto-verified
            </span>
          )}
          {checked_at && (
            <span className="text-[10px]" style={{ color: "#64748B" }}>
              · {new Date(checked_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 mt-1 text-[12px]" style={{ color: "#0F1B2D" }}>
          {linkedin_name && (
            <div>
              <span className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: "#0A66C2", fontWeight: 700 }}>Name</span>
              <div>{linkedin_name}</div>
            </div>
          )}
          {linkedin_email && (
            <div>
              <span className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: "#0A66C2", fontWeight: 700 }}>
                LinkedIn email
                {linkedin_email_verified && (
                  <CheckCircle2 size={9} style={{ color: "#10B981", display: "inline", marginLeft: 3 }} />
                )}
              </span>
              <div className="truncate" title={linkedin_email}>{linkedin_email}</div>
            </div>
          )}
        </div>
        {match_reason === "no_match" && (
          <div className="mt-1 flex items-start gap-1 text-[11px]" style={{ color: "#7F1D1D" }}>
            <AlertTriangle size={11} className="shrink-0 mt-0.5" />
            <span>
              LinkedIn email doesn&apos;t match signup domain — verify manually if this is a legitimate recruiter.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

